import {
	Component,
	OnInit,
	Input,
	OnDestroy,
	ViewEncapsulation,
	OnChanges,
	SimpleChanges,
	AfterViewInit,
	Output,
	EventEmitter,
	HostBinding,
	ContentChildren,
	QueryList,
	AfterContentInit
} from '@angular/core';
import { IdService } from '../../../shared/services/id.service';
import { SidenavService } from '../sidenav.service';
import { Subscription } from 'rxjs';

export interface SidenavItemEvent {
	id: string;
	self: SidenavItemComponent;
}
export interface SidenavConfig {
	name: string;
	showInMenu?: boolean;
	displayName?: string;
	icon?: string;
	order?: number;
	children?: SidenavConfig[];
	link?: string;
	// check the Angular RouterLink directive docs for more information
	routerLinkOptions?: SidenavRoute;
	anchorLinks?: SidenavAnchorLink[];
}

export interface SidenavRoute {
	to: string | any[];
	queryParams?: { [k: string]: any };
	preserveQueryParams?: boolean;
	preserveFragment?: boolean;
	queryParamsHandling?: 'merge' | 'preserve' | '';
	replaceUrl?: boolean;
	skipLocationChange?: boolean;
	fragment?: string;
	activeOptions?: {
		exact: boolean;
	};
}

export interface SidenavAnchorLink {
	link: string;
	text: string;
}

@Component({
	selector: '[rbc-side-nav-item]',
	templateUrl: './sidenav-item.component.html',
	styleUrls: ['./sidenav-item.component.scss'],
	encapsulation: ViewEncapsulation.None,
	host: {
		role: 'none'
	}
})
export class SidenavItemComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit, AfterContentInit {
	@HostBinding('id') @Input() id: string;
	@HostBinding('class') @Input('class') classList: string = '';
	@Input() active: boolean;
	@Input() expandable: boolean = false;
	@Input() isExpanded: boolean = false;
	@Input() header: string;
	@Input() href: string;
	@Input() icon: string;
	@Input() iconWeight: 'solid' | 'regular' | 'light' = 'regular';
	@Input() routeName: string;
	@Input() router: boolean;
	@Input() routerOptions: SidenavRoute;
	@Input() routerTo: object;
	@Input() target: string = '';
	@Input() anchorLinks: string[];

	@Output() isExpandedChange = new EventEmitter<boolean>();
	@Output() sidenavupdate = new EventEmitter<SidenavItemEvent>();

	@ContentChildren(SidenavItemComponent) items: QueryList<SidenavItemComponent>;

	$activeSub: Subscription;
	defaultMessage = 'Default';

	get showIcon(): boolean {
		return typeof this.icon != 'undefined' && this.icon && this.icon !== '';
	}

	constructor(private idService: IdService, private sidenavService: SidenavService) {}

	ngOnInit() {
		this.id = this.id ? this.id : this.idService.generate();
		this.classList = [
			this.classList,
			'nav-item',
			this.icon ? 'nav-item-icon' : null,
			this.anchorLinks ? 'nav-item-anchor-active' : null
		]
			.filter(i => !!i)
			.join(' ');

		this.mountActiveItemListener();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes) {
			if (changes.isExpanded && !changes.isExpanded.firstChange) {
				this.isExpandedChange.emit(this.isExpanded);
			}
		}
	}

	ngOnDestroy(): void {}

	ngAfterViewInit() {
		this.checkRouterLink();
	}

	ngAfterContentInit(): void {
		// add self to the service
		this.syncWithService();
	}

	mountActiveItemListener() {
		// each item listens to the service for changes to the active item
		this.$activeSub = this.sidenavService.$activeItemId.subscribe(activeItemId => {
			this.isExpanded = this.id === activeItemId;
			this.active = this.isExpanded;
		});
	}

	// let the service know of prescence
	syncWithService() {
		this.sidenavService.addItem(this);
	}

	checkRouterLink() {
		// for side nav using the router, see if expand should be open upon route update
		if (this.router) {
			// save the currentPage info from the parent
			const current = this.sidenavService.getCurrentRouteSnapshot();
			// check if the current page matches the side nav item route name
			if (current && current.url.length > 0 && current.url[0].path === this.routeName) {
				this.isExpanded = true;
			}
		}
	}

	expandableOpened() {
		this.emitItemClicked(true);
	}

	expandableClosed() {
		this.emitItemClicked(false);
	}

	/**
	 * notify the service of an interaction
	 * @param toggled true => declaring that this item is the current active item
	 */
	emitItemClicked(toggled: boolean) {
		this.sidenavupdate.emit({
			id: this.id,
			self: this
		});
		// let parent know that a new active link has been selected
		this.sidenavService.$eventBus.next({
			activeItemId: toggled ? this.id : null,
			isDirectChild: this.sidenavService.checkIfDirectChild(this),
			item: this
		});
	}
}
